import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { FundOrder, getFundOrders } from "../../services/get-fund-orders";
import { Fund, getFunds } from "../../services/get-funds";
import { getEnv } from "@ace/env";
import { sessionStorageKeys } from "../../constants";
import {
  AltTradingPair,
  getAllTradingPairs,
} from "../../services/get-all-trading-pairs";
import { getErrorMessage } from "../../utils/get-error-message";
import { hasOneOfRole } from "@archax/web-lib";

interface FundsContextValue {
  funds: Fund[];
  fundsLoading: boolean;

  fundOrders: FundOrder[];
  fundOrdersLoading: boolean;

  altTradingPairs: AltTradingPair[];
  altTradingPairsLoading: boolean;

  submitFundOrder: (
    type: "subscribe" | "redeem",
    investmentId: number,
    organisationId: number,
    amount: string,
  ) => Promise<any>;

  cancelFundOrder: (orderId: string, organisationId: number) => Promise<any>;

  refreshOrders: () => Promise<void>;
}

class FundOrderError extends Error {
  constructor(message: string, statusCode: number) {
    super(message);
    this.statusCode = statusCode;
  }

  statusCode: number;
}

const FundsContext = createContext<FundsContextValue>({} as FundsContextValue);

const FundsContextProvider = FundsContext.Provider;

const FundsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [fundOrders, setFundOrders] = useState<FundOrder[]>([]);
  const [funds, setFunds] = useState<Fund[]>([]);
  const [altTradingPairs, setAltTradingPairs] = useState<AltTradingPair[]>([]);
  const [fundOrdersLoading, setFundOrdersLoading] = useState(true);
  const [fundsLoading, setFundsLoading] = useState(true);
  const [altTradingPairsLoading, setAltTradingPairsLoading] = useState(true);

  const hasInvestRole = useMemo(() => {
    const token = sessionStorage.getItem(sessionStorageKeys.token);
    if (!token) return false;
    return hasOneOfRole(["invest", "ro:invest"], token);
  }, []);

  const fetchOrders = async () => {
    if (!hasInvestRole) {
      setFundOrdersLoading(false);
      return;
    }
    try {
      const orders = await getFundOrders();
      setFundOrders(orders);
      setFundOrdersLoading(false);
    } catch (error) {
      console.error("Error fetching fund orders:", error);
      setFundOrdersLoading(false);
    }
  };

  const fetchFunds = async () => {
    if (!hasInvestRole) {
      setFundsLoading(false);
      return;
    }
    try {
      const investments = await getFunds();
      setFunds(investments);
      setFundsLoading(false);
    } catch (error) {
      setFundsLoading(false);
      console.error("Error fetching funds:", error);
    }
  };

  const fetchTradingPairs = async () => {
    try {
      const tradingPairs = await getAllTradingPairs();
      setAltTradingPairs(tradingPairs);
      setAltTradingPairsLoading(false);
    } catch (error) {
      console.error("Error fetching trading pairs:", error);
      setAltTradingPairsLoading(false);
    }
  };

  useEffect(() => {
    fetchFunds();
    fetchOrders();
    fetchTradingPairs();

    // Cleanup function to avoid memory leaks
    return () => {
      setFundOrders([]);
      setFunds([]);
      setAltTradingPairs([]);
      setFundOrdersLoading(true);
      setFundsLoading(true);
      setAltTradingPairsLoading(true);
    };
  }, []);

  const refreshOrders = async () => {
    await fetchOrders();
  };

  const submitFundOrder = async (
    type: "subscribe" | "redeem",
    investmentId: number,
    organisationId: number,
    amount: string,
  ) => {
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${sessionStorage.getItem(
        sessionStorageKeys.token,
      )}`,
    };
    const response = await fetch(
      `${getEnv("VITE_ACE_WEB_AUTH_SERVER")}/rest-gateway/invest/order/${type}`,
      {
        method: "POST",
        headers,
        body: JSON.stringify({
          investmentId: investmentId,
          organisationId: organisationId,
          amount,
        }),
      },
    );

    const json = await response.json();
    if (json.status === "ERROR") {
      throw new FundOrderError(
        getErrorMessage(json.error[0].msg),
        response.status,
      );
    }
    await fetchOrders();

    return json.data;
  };

  const cancelFundOrder = async (orderId: string, organisationId: number) => {
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${sessionStorage.getItem(
        sessionStorageKeys.token,
      )}`,
    };
    const response = await fetch(
      `${getEnv(
        "VITE_ACE_WEB_AUTH_SERVER",
      )}/rest-gateway/invest/order/${orderId}/cancel`,
      {
        method: "POST",
        headers,
        body: JSON.stringify({
          organisationId,
        }),
      },
    );

    const json = await response.json();
    if (json.status === "ERROR") {
      const errorMessage = getErrorMessage(json.error[0].msg);
      throw new Error(errorMessage);
    }

    // update orders after cancellation
    await fetchOrders();

    return json.data;
  };

  return (
    <FundsContextProvider
      value={{
        funds,
        fundsLoading,
        fundOrders,
        fundOrdersLoading,
        altTradingPairs,
        altTradingPairsLoading,
        submitFundOrder,
        cancelFundOrder,
        refreshOrders,
      }}
    >
      {children}
    </FundsContextProvider>
  );
};

const useFunds = () => {
  const {
    funds,
    fundsLoading,
    fundOrders,
    fundOrdersLoading,
    altTradingPairs,
    altTradingPairsLoading,
    submitFundOrder,
    cancelFundOrder,
    refreshOrders,
  } = useContext(FundsContext);
  return {
    funds,
    fundsLoading,
    fundOrders,
    fundOrdersLoading,
    altTradingPairs,
    altTradingPairsLoading,
    submitFundOrder,
    cancelFundOrder,
    refreshOrders,
  };
};

export { FundsProvider, useFunds };
